import React from "react";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import createIOSortType from "../../../../../../lib/createIOSortType";
import { useMetrologyCrossFilters } from "../../../../../../api/useMetrologyCrossFilters";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import useSortedData from "../../../../../../lib/hooks/useSortedData";
import type { SortableValue } from "diagnostics-lib/comparator";
import Section from "@ui/Section";
import t from "../../../../../../i18n";
import TableCard from "@ui/Table/Card";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import { useQuantities, type Quantity } from "../../../../../../api/useQuantities";
import BlankStateV2 from "@ui/BlankStateV2";
import { useCanUserDoOnProject } from "../../../../../canUserDo";
import { useModals } from "@ui/lib/hooks/useModal";
import Modal from "@ui/Modal";
import { ImportCrossFilterModalForm } from "./Import";
import { DeleteCrossFiltersModalForm } from "./Delete";
import { styled } from "@linaria/react";
import { useGet } from "../../../../../../api/useCall";
import download from "downloadjs";
import { generateExportFileName } from "diagnostics-lib/string";
import { colors } from "@ui/Theme";
import { DeleteBinLine, DownloadLine, More2Fill, UploadLine } from "@ui/IconV2/react";
import Button from "@ui/Button";
import { color, fonts } from "@ui";
import Menu from "@ui/Menu";
import Option, { OptionGroup } from "@ui/Option";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { TableCardWithoutBorder } from "@ui/Table/_styles/TableCardWithoutBorder";
import { LoadedFileName } from "../../../LoadedFileName";
import { useOutletContext } from "react-router";
import type { QuantityLayoutOutletContext } from "../..";

const StyledMenu = styled(Menu)`
  font: ${fonts.body2};
`;

const StyledBlankStateV2 = styled(BlankStateV2)`
  margin-top: 24px;
  background-color: ${colors.hydrogen};
  border: 1px solid ${colors.titanium200};
  border-radius: 4px;
`;

type MetrologyCrossFilter = {
  min: number | null;
  max: number | null;
  maxStd: number | null;
  invalidatedQuantityName: string;
  evaluatedQuantityName: string;
};

type MetrologyCrossFilterInput = {
  min: number | null;
  max: number | null;
  maxStd: number | null;
  invalidatedQuantityName: string;
  evaluatedQuantityName: string;
};

const useInputCrossFilterSearch = createGlobalState({
  storage: "session",
  key: "inputCrossFilterSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});

const columns = [
  "invalidatedQuantityName",
  "evaluatedQuantityName",
  "min",
  "max",
  "maxStd",
] as const;
type Column = (typeof columns)[number];

const useSortInput = createGlobalState({
  storage: "session",
  key: "crossFilterSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "invalidatedQuantityName",
    direction: "asc",
  },
  scoped: true,
});

const computeInputs = (crossFilters: MetrologyCrossFilter[]) =>
  crossFilters.map(input => ({
    invalidatedQuantityName: input.invalidatedQuantityName,
    evaluatedQuantityName: input.evaluatedQuantityName,
    min: input.min,
    max: input.max,
    maxStd: input.maxStd,
  }));

const sortSelector: { [key in Column]: (b: MetrologyCrossFilterInput) => SortableValue | null } = {
  invalidatedQuantityName: b => b.invalidatedQuantityName,
  evaluatedQuantityName: b => b.evaluatedQuantityName,
  min: b => b.min,
  max: b => b.max,
  maxStd: b => b.maxStd,
};

const computeRowKey = (crossFilter: MetrologyCrossFilterInput) =>
  `${crossFilter.invalidatedQuantityName}-${crossFilter.evaluatedQuantityName}`;

export const CrossFilters = () => {
  const { project } = useOutletContext<QuantityLayoutOutletContext>();
  if (!project) {
    throw new Error("no project");
  }

  const { modal, closeModal, openModal } = useModals<string>();
  const exportCrossFilters = useGet(
    "/projects/:projectId(uuid)/quantities/crossFiltersMetrology/export",
  );
  const canDoAction = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const quantities = useQuantities(project.id);
  const quantityNameIndex = React.useMemo(() => {
    const index: { [quantityName: string]: Quantity } = {};
    for (const q of quantities) {
      index[q.name] = q;
    }
    return index;
  }, [quantities]);

  const [search, setSearch] = useInputCrossFilterSearch({ scope: project.id });
  const [sort, setSort] = useSortInput({ scope: project.id });
  const crossFiltersData = useMetrologyCrossFilters(project.id);
  const crossFiltersInputs = React.useMemo(
    () => computeInputs(crossFiltersData),
    [crossFiltersData],
  );

  const [filteredData] = useFilteredDatas(
    crossFiltersInputs,
    search,
    input => `${input.invalidatedQuantityName} ${input.evaluatedQuantityName}`,
  );

  const sortedCrossFilters = useSortedData(filteredData, sortSelector[sort.column], sort.direction);

  const handleExport = React.useCallback(async () => {
    const res = await exportCrossFilters({ routeParams: { projectId: project.id } });

    download(
      res.body as unknown as Blob,
      generateExportFileName({
        filename: "cross_filters",
        projectName: project.name,
        extension: "csv",
      }),
    );
  }, [exportCrossFilters, project.name, project.id]);

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  const inputMetadata: { [key in Column]: ColumnMetaData<MetrologyCrossFilterInput> } = {
    invalidatedQuantityName: {
      label: t("quantities.crossFilter.table.invalidatedQuantity"),
      render: (b: MetrologyCrossFilterInput) => b.invalidatedQuantityName,
      minWidth: "200px",
      sortable: true,
    },
    evaluatedQuantityName: {
      label: t("quantities.crossFilter.table.evaluatedQuantity"),
      render: (b: MetrologyCrossFilterInput) => b.evaluatedQuantityName,
      minWidth: "200px",
      sortable: true,
    },
    min: {
      label: t("quantities.crossFilter.table.min"),
      render: (b: MetrologyCrossFilterInput) =>
        b.min !== null && quantityNameIndex[b.evaluatedQuantityName]?.unit
          ? b.min + " " + quantityNameIndex[b.evaluatedQuantityName]?.unit
          : b.min,
      minWidth: "100px",
      sortable: true,
    },
    max: {
      label: t("quantities.crossFilter.table.max"),
      render: (b: MetrologyCrossFilterInput) =>
        b.max !== null && quantityNameIndex[b.evaluatedQuantityName]?.unit
          ? b.max + " " + quantityNameIndex[b.evaluatedQuantityName]?.unit
          : b.max,
      minWidth: "100px",
      sortable: true,
    },
    maxStd: {
      label: t("quantities.crossFilter.table.maxStd"),
      render: (b: MetrologyCrossFilterInput) =>
        b.maxStd !== null && quantityNameIndex[b.evaluatedQuantityName]?.unit
          ? b.maxStd + " " + quantityNameIndex[b.evaluatedQuantityName]?.unit
          : b.maxStd,
      minWidth: "100px",
      sortable: true,
    },
  };

  if (crossFiltersData.length === 0) {
    return (
      <>
        <LoadedFileName fileName={project.crossQuantityInvalidityFiltersFileName} />
        <StyledBlankStateV2
          title={t("quantities.crossFilter.blankState.title")}
          subtitle={canDoAction ? t("quantities.crossFilter.blankState.subtitle") : null}
          onClick={canDoAction ? () => openModal("import") : undefined}
          buttonLabel={
            canDoAction ? t("quantities.crossFilter.blankState.button_label") : undefined
          }
          buttonPicto={<UploadLine />}
        />
        <Modal
          title={t("quantities.crossFilter.modal.import.title")}
          isOpen={modal === "import"}
          onRequestClose={closeModal}
        >
          <ImportCrossFilterModalForm project={project} onRequestClose={closeModal} />
        </Modal>
      </>
    );
  }

  return (
    <>
      <LoadedFileName fileName={project.crossQuantityInvalidityFiltersFileName} />
      <Section id="crossFilters" title="">
        <TableCard
          className={TableCardWithoutBorder}
          title={null}
          headerLeft={
            <>
              <SearchInputV2
                placeholder={t("commons.placeholder.search")}
                value={search}
                onChange={handleSearchChange}
              />
            </>
          }
          headerRight={
            <>
              {canDoAction ? (
                <>
                  <Button
                    variant="primary"
                    onClick={() => openModal("replace")}
                    leftIcon={<UploadLine />}
                  >
                    {t("commons.message_card.replace")}
                  </Button>
                  <StyledMenu
                    arrowWidth={0}
                    // gap={16}
                    placement="bottom-end"
                    overlay={
                      <>
                        <OptionGroup>
                          <Option closeOnClick onClick={handleExport}>
                            <DownloadLine color={color.carbon800} />
                            {t("commons.message_card.export")}
                          </Option>
                          <Option
                            closeOnClick
                            onClick={() => openModal("delete")}
                            disabled={!canDoAction}
                          >
                            <DeleteBinLine color={color.carbon800} />
                            {t("commons.message_card.delete")}
                          </Option>
                        </OptionGroup>
                      </>
                    }
                  >
                    <ButtonIconV2>
                      <More2Fill size="m" />
                    </ButtonIconV2>
                  </StyledMenu>
                </>
              ) : null}
            </>
          }
        >
          <Table
            stickyHeader
            datas={sortedCrossFilters}
            metadata={inputMetadata}
            sort={sort}
            onSort={setSort}
            computeRowKey={computeRowKey}
          />
        </TableCard>
      </Section>
      <Modal
        title={t("quantities.crossFilter.modal.import.title")}
        isOpen={modal === "replace"}
        onRequestClose={closeModal}
      >
        <ImportCrossFilterModalForm project={project} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("quantities.crossFilter.modal.delete.title")}
        isOpen={modal === "delete"}
        onRequestClose={closeModal}
      >
        <DeleteCrossFiltersModalForm projectId={project.id} onRequestClose={closeModal} />
      </Modal>
    </>
  );
};
